// libraries
// react
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import {
  type GetEnergyGroupDto,
  type GetEnergyInitiativeDto,
  usePostEnergyGroupInitiativeMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFTextField } from '@/components/ui/form';

// relative
import { toPostEnergyInitiativeDto } from './initiativeForm.mappers';
import { createInitiativeFormSchema } from './initiativeForm.schema';
import InitiativePeriodsSection from './InitiativePeriodsSection';
import type { InitiativePeriodsSectionHandle } from './InitiativePeriodsSection';
import InitiativeSourceSelector from './InitiativeSourceSelector';

import { zodResolver } from '@hookform/resolvers/zod';

export default function CreateInitiativeForm({
  energyGroup,
  onSuccess,
  onCancel,
}: {
  energyGroup?: GetEnergyGroupDto;
  onSuccess: (initiative: GetEnergyInitiativeDto) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);

  const [postEnergyInitiative, { isLoading: isCreating }] =
    usePostEnergyGroupInitiativeMutation();

  const periodsRef = useRef<InitiativePeriodsSectionHandle>(null);

  const schema = createInitiativeFormSchema(t);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      energyTreeNodes: [],
      baselineStart: null,
      baselineEnd: null,
      comparisonStart: null,
      comparisonEnd: null,
    },
    mode: 'onTouched',
  });

  const energyTreeNodes = useWatch({ control, name: 'energyTreeNodes' });

  const onSubmit = handleSubmit(async (values) => {
    if (!energyGroup?.energyGroupId) return;

    const result = await postEnergyInitiative({
      groupId: energyGroup.energyGroupId,
      postEnergyInitiativeDto: toPostEnergyInitiativeDto(values),
    });
    if ('error' in result) return;

    onSuccess(result.data);
    // Errors are handled by apiErrorMiddleware (snackbar + App Insights).
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="initiative-create-form">
      <Stack spacing={2}>
        <Card variant="outlined" data-testid="initiative-create-card">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                label={t('createInitiative.name.label', { ns: 'forms' })}
                placeholder={t('createInitiative.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createInitiative.name.helperText', {
                  ns: 'forms',
                })}
                disabled={isCreating}
                data-testid="create-initiative-name-input"
              />

              <RHFTextField
                name="description"
                control={control}
                fullWidth
                multiline
                minRows={2}
                label={t('createInitiative.description.label', {
                  ns: 'forms',
                })}
                placeholder={t('createInitiative.description.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createInitiative.description.helperText', {
                  ns: 'forms',
                })}
                disabled={isCreating}
                data-testid="create-initiative-description-input"
              />

              <InitiativeSourceSelector
                control={control}
                nodes={energyGroup?.energyTreeNodes ?? []}
                disabled={isCreating}
                onSourcesBlur={(nodeIds) =>
                  periodsRef.current?.triggerAutoFetch(nodeIds)
                }
              />
              {energyTreeNodes.length > 0 && (
                <InitiativePeriodsSection
                  ref={periodsRef}
                  control={control}
                  setValue={setValue}
                  energyGroupId={energyGroup?.energyGroupId}
                  accountId={energyGroup?.accountId}
                  totalNodesInGroup={energyGroup?.energyTreeNodes?.length ?? 0}
                  disabled={isCreating}
                />
              )}
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isCreating}
            data-testid="initiative-create-cancel-button"
          >
            {t('common.cancel', { ns: 'forms' })}
          </CancelButton>
          <SubmitButton loading={isCreating} disabled={isCreating || !isValid}>
            {isCreating
              ? t('createInitiative.submitting', { ns: 'forms' })
              : t('createInitiative.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
